import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Columns3, Grid, Clock, User, Phone } from 'lucide-react';
import { format, isSameDay, isSameMonth, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek } from 'date-fns';

const daysOfWeek = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const Day = ({ day, isCurrentMonth, meetingCount, onHover, onClick, isSelected }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      className={`relative flex items-center justify-center py-1 transition-all ${
        !isCurrentMonth 
          ? 'bg-gray-50 text-gray-400 cursor-not-allowed' 
          : isSelected
            ? 'bg-gray-900 shadow-md ring-2 ring-gray-900 ring-offset-2 cursor-pointer z-10'
            : 'bg-white border border-gray-100 shadow-sm hover:border-gray-300 hover:shadow-md cursor-pointer'
      }`}
      style={{ height: '4.5rem', borderRadius: 16 }}
      onMouseEnter={() => {
        setIsHovered(true);
        if (isCurrentMonth) onHover(day);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        onHover(null);
      }}
      onClick={() => isCurrentMonth && onClick(day)}
    >
      <motion.div className="flex flex-col items-center justify-center">
        <span className={`text-[15px] font-bold ${
          !isCurrentMonth ? 'text-gray-400' : isSelected ? 'text-white' : 'text-gray-900'
        }`}>
          {format(day, 'd')}
        </span>
      </motion.div>
      
      {meetingCount > 0 && (
        <motion.div
          className={`absolute bottom-1.5 right-1.5 flex size-5 items-center justify-center rounded-full text-[10px] font-bold ${
            isSelected ? 'bg-white text-gray-900' : 'bg-gray-900 text-white'
          }`}
        >
          {meetingCount}
        </motion.div>
      )}

      <AnimatePresence>
        {meetingCount > 0 && isHovered && !isSelected && (
          <div className="absolute inset-0 flex size-full items-center justify-center z-20">
            <motion.div
              className="flex size-10 items-center justify-center bg-gray-900 p-1 text-xs font-bold text-white shadow-xl"
              style={{ borderRadius: 999 }}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              {meetingCount}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CalendarGrid = ({ days, currentDate, selectedDate, bookingsData, onHover, onClick }) => {
  return (
    <div className="grid grid-cols-7 gap-2">
      {days.map((day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const dayBookings = bookingsData.filter(b => {
            const bDate = b.metadata?.booking_date || b.booking_date || b.created_at?.split('T')[0];
            return bDate === dateStr;
        });
        
        return (
          <Day
            key={day.toISOString()}
            day={day}
            isCurrentMonth={isSameMonth(day, currentDate)}
            isSelected={isSameDay(day, selectedDate)}
            meetingCount={dayBookings.length}
            onHover={onHover}
            onClick={onClick}
          />
        );
      })}
    </div>
  );
};

const InteractiveCalendar = React.forwardRef(({ 
  className, 
  selectedDate, 
  onSelectDate, 
  bookingsData = [],
  serviceName = "Service",
  children,
  ...props 
}, ref) => {
  const [moreView, setMoreView] = useState(true);
  const [hoveredDay, setHoveredDay] = useState(null);

  const currentDate = selectedDate || new Date();
  
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [currentDate]);

  const handleDayHover = (day) => {
    setHoveredDay(day);
  };

  const activeDate = hoveredDay || selectedDate;
  const activeDateStr = activeDate ? format(activeDate, 'yyyy-MM-dd') : null;
  const activeBookings = useMemo(() => {
    if (!activeDateStr) return [];
    return bookingsData.filter(b => {
        const bDate = b.metadata?.booking_date || b.booking_date || b.created_at?.split('T')[0];
        return bDate === activeDateStr;
    });
  }, [activeDateStr, bookingsData]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        ref={ref}
        className={`relative mx-auto flex w-full flex-col items-start justify-center gap-8 xl:flex-row ${className}`}
        {...props}
      >
        <motion.div layout className="w-full max-w-2xl flex-shrink-0">
          <motion.div
            key="calendar-view"
            className="flex w-full flex-col gap-6 bg-white p-6 sm:p-8 rounded-[32px] border border-gray-100 shadow-sm"
          >
            <div className="flex w-full items-center justify-between">
              <div>
                <motion.h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
                  {format(currentDate, 'MMMM')} <span className="text-gray-400">{format(currentDate, 'yyyy')}</span>
                </motion.h2>
              </div>
              <motion.button
                className="relative flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-1.5 py-1 text-gray-600 shadow-inner"
                onClick={() => setMoreView(!moreView)}
              >
                <Columns3 className="z-[2] w-4 h-4 m-1" />
                <Grid className="z-[2] w-4 h-4 m-1" />
                <div
                  className="absolute left-0 top-0 h-[85%] w-7 rounded-lg bg-white shadow-sm transition-transform duration-300"
                  style={{
                    top: '50%',
                    transform: moreView
                      ? 'translateY(-50%) translateX(36px)'
                      : 'translateY(-50%) translateX(4px)',
                  }}
                ></div>
              </motion.button>
            </div>
            
            <div className="grid grid-cols-7 gap-2">
              {daysOfWeek.map((day) => (
                <div
                  key={day}
                  className="rounded-xl bg-gray-100 py-2 text-center text-xs font-bold tracking-wider text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>
            
            <CalendarGrid 
              days={calendarDays} 
              currentDate={currentDate} 
              selectedDate={selectedDate} 
              bookingsData={bookingsData}
              onHover={handleDayHover}
              onClick={onSelectDate}
            />
          </motion.div>
        </motion.div>
        
        {moreView && (
          <motion.div
            className="w-full max-w-3xl flex-grow"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              key="more-view"
              className="flex w-full flex-col gap-6"
            >
              <div className="flex w-full flex-col items-start justify-between px-2">
                <motion.h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
                  {activeDate ? format(activeDate, 'MMM d') : 'Bookings'}
                </motion.h2>
                <p className="font-medium text-gray-500 mt-2">
                  {activeBookings.length} booking{activeBookings.length !== 1 ? 's' : ''} on {activeDate ? format(activeDate, 'EEEE') : 'this day'}
                </p>
              </div>
              
              <motion.div
                className="flex flex-col items-start justify-start w-full"
                layout
              >
                {children}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
});

InteractiveCalendar.displayName = 'InteractiveCalendar';

export default InteractiveCalendar;
